// Move in from Fullbay (or any other system): upload an Excel/CSV export, match the columns, check, import.
import { useState } from 'react';
import { api, navigate, dollars } from '../lib';
import { Badge, Button, Panel, Table, Icon, Select, useToast } from '../ui';
import { IMPORT_KINDS, guessMapping, type ImportKind } from '../../shared/importdefs';
import { readSheet, type Sheet } from '../sheet';
import { fromFullbay, FB_LABEL, type FullbayResult } from '../../shared/fullbay';
import { historyFromFullbay, historyKind, lastPrices, HIST_LABEL, type HistInvoice } from '../../shared/fullbayHistory';

type Result = { created: number; updated: number; skipped: number; errors: { row: number | string; error: string }[]; newCustomers?: number; owed?: number; payments?: number };
const CHUNK = 250;
const LINKS: Record<ImportKind, string> = { customers: '/customers', units: '/customers', parts: '/inventory', vendors: '/inventory?tab=vendors' };

export function ImportPage() {
  const [kind, setKind] = useState<ImportKind>('customers');
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [fileName, setFileName] = useState('');
  const [map, setMap] = useState<Record<string, number>>({});
  const [check, setCheck] = useState<Result | null>(null);
  const [done, setDone] = useState<Result | null>(null);
  const [busy, setBusy] = useState('');
  const toast = useToast();
  const def = IMPORT_KINDS.find(k => k.kind === kind)!;

  const pick = (k: ImportKind) => { setKind(k); setCheck(null); setDone(null); if (sheet) setMap(guessMapping(k, sheet.headers)); };
  async function load(f?: File) {
    if (!f) return;
    try {
      const s = await readSheet(f);
      setSheet(s); setFileName(f.name); setCheck(null); setDone(null); setMap(guessMapping(kind, s.headers));
    } catch (e) { toast((e as Error).message, 'err'); }
  }
  const mapped = () => (sheet?.rows || []).map(r => Object.fromEntries(Object.entries(map).filter(([, i]) => i >= 0).map(([k, i]) => [k, r[i] ?? ''])));
  const missing = def.fields.filter(f => f.required && !(map[f.key] >= 0));

  async function run(dry: boolean) {
    const rows = mapped();
    const total: Result = { created: 0, updated: 0, skipped: 0, errors: [], newCustomers: 0 };
    for (let i = 0; i < rows.length; i += CHUNK) {
      setBusy(`${dry ? 'Checking' : 'Importing'} ${Math.min(i + CHUNK, rows.length)} of ${rows.length}…`);
      const r = await api<Result>(`/api/import/${kind}`, { body: { rows: rows.slice(i, i + CHUNK), dry, first: i + 2 } });
      total.created += r.created; total.updated += r.updated; total.skipped += r.skipped; total.errors.push(...r.errors); total.newCustomers! += r.newCustomers || 0;
    }
    return total;
  }

  return (
    <>
      <div className="page-head">
        <div>
          <a className="back" href="/settings" onClick={e => { e.preventDefault(); navigate('/settings'); }}><Icon name="back" size={16} />Settings</a>
          <h1>Import data</h1>
          <p>Moving from Fullbay or another system? Export each list to Excel or CSV there, then bring it in here. Nothing is added twice — run it again any time.</p>
        </div>
      </div>

      <FullbayImport />

      <Panel title="Not from Fullbay? Import one list from any Excel / CSV — 1. What are you importing?">
        <div className="import-kinds pad">
          {IMPORT_KINDS.map((k, i) => (
            <button key={k.kind} className={'import-kind' + (k.kind === kind ? ' on' : '')} onClick={() => pick(k.kind)}>
              <b>{k.label}</b><span>{k.about}</span>{i === 0 && <small className="muted">Best order: Customers → Trucks & trailers → Vendors → Parts</small>}
            </button>
          ))}
        </div>
      </Panel>

      <Panel title="2. Pick the file">
        <div className="pad">
          <label className="btn secondary file-pick"><Icon name="download" size={16} />{fileName ? 'Pick another file' : 'Choose Excel or CSV file'}
            <input type="file" accept=".csv,.xlsx,.txt,.tsv,text/csv" hidden onChange={e => { void load(e.target.files?.[0]); e.target.value = ''; }} /></label>
          {sheet && <span className="muted"> &nbsp;{fileName} · <b>{sheet.rows.length}</b> rows · {sheet.headers.length} columns</span>}
          <p className="form-note">From another system or your own spreadsheet: export the list to Excel or CSV. Excel (.xlsx) and CSV both work. Coming from Fullbay? Use the box above instead — it brings everything at once.</p>
        </div>
      </Panel>

      {sheet && (
        <Panel title="3. Match the columns">
          <div className="map-grid pad">
            {def.fields.map(f => (
              <Select key={f.key} label={f.label + (f.required ? ' *' : '')} name={'m_' + f.key} value={String(map[f.key] ?? -1)} hint={f.hint}
                onChange={e => { setCheck(null); setDone(null); setMap(m => ({ ...m, [f.key]: Number(e.target.value) })); }}>
                <option value="-1">— not in the file —</option>
                {sheet.headers.map((h, i) => <option key={i} value={i}>{h}{sheet.rows[0]?.[i] ? `  (e.g. ${sheet.rows[0][i].slice(0, 24)})` : ''}</option>)}
              </Select>
            ))}
          </div>
          {missing.length > 0 && <p className="pad danger">Pick a column for: {missing.map(f => f.label).join(', ')}</p>}
          <div className="pad"><b>First rows as they will come in:</b></div>
          <Table heads={def.fields.filter(f => map[f.key] >= 0).map(f => f.label)}
            rows={mapped().slice(0, 5).map(r => def.fields.filter(f => map[f.key] >= 0).map(f => r[f.key] || <span className="muted">—</span>))} />
        </Panel>
      )}

      {sheet && !missing.length && (
        <Panel title="4. Check, then import">
          <div className="pad">
            {busy ? <p><b>{busy}</b></p> : !check ? (
              <Button kind="primary" onClick={async () => { try { setCheck(await run(true)); } catch (e) { toast((e as Error).message, 'err'); } finally { setBusy(''); } }}>Check {sheet.rows.length} rows (nothing is saved yet)</Button>
            ) : !done ? (
              <>
                <p><Badge tone="green">{check.created} new</Badge> <Badge tone="blue">{check.updated} will be updated</Badge> <Badge tone="gray">{check.skipped} already the same</Badge>
                  {check.errors.length > 0 && <> <Badge tone="red">{check.errors.length} can't be imported</Badge></>}
                  {!!check.newCustomers && <> · {check.newCustomers} customers will be created for these units</>}</p>
                <ErrorList r={check} />
                <div className="actions">
                  <Button kind="primary" disabled={!check.created && !check.updated} onClick={async () => {
                    try { const r = await run(false); setDone(r); toast(`Imported: ${r.created} new, ${r.updated} updated`); } catch (e) { toast((e as Error).message, 'err'); } finally { setBusy(''); }
                  }}>Import now</Button>
                  <Button onClick={() => setCheck(null)}>Back</Button>
                </div>
              </>
            ) : (
              <>
                <p>✅ Done — <b>{done.created}</b> added, <b>{done.updated}</b> updated, {done.skipped} unchanged{done.errors.length ? `, ${done.errors.length} skipped with problems` : ''}.</p>
                <ErrorList r={done} />
                <div className="actions"><Button kind="primary" onClick={() => navigate(LINKS[kind])}>Open {def.label.toLowerCase()}</Button>
                  <Button onClick={() => { setSheet(null); setFileName(''); setCheck(null); setDone(null); }}>Import another file</Button></div>
              </>
            )}
          </div>
        </Panel>
      )}
    </>
  );
}

function ErrorList({ r }: { r: Result }) {
  if (!r.errors.length) return null;
  return (
    <details className="pad"><summary>Rows with problems ({r.errors.length})</summary>
      <ul className="list small">{r.errors.slice(0, 100).map(e => <li key={e.row}><span>Row {e.row}</span><span className="muted right">{e.error}</span></li>)}</ul>
    </details>
  );
}

const FB_ORDER: { kind: ImportKind; label: string }[] = [
  { kind: 'vendors', label: 'Vendors' }, { kind: 'customers', label: 'Customers' }, { kind: 'units', label: 'Trucks & trailers' }, { kind: 'parts', label: 'Parts' },
];

/** Fullbay → Asena Bay in one go: pick every file from Fullbay's Manage Data page at once. */
function FullbayImport() {
  const [fb, setFb] = useState<FullbayResult | null>(null);
  const [hist, setHist] = useState<ReturnType<typeof historyFromFullbay> | null>(null);
  const [prices, setPrices] = useState<{ sku: string; price: number }[]>([]);
  const [fill, setFill] = useState<{ filled: number; stillEmpty: number; done?: boolean } | null>(null);
  const [kinds, setKinds] = useState<Record<string, string>>({});
  const [check, setCheck] = useState<Record<string, Result> | null>(null);
  const [done, setDone] = useState<Record<string, Result> | null>(null);
  const [busy, setBusy] = useState('');
  const toast = useToast();
  async function load(list: FileList | null) {
    if (!list?.length) return;
    try {
      const sheets = await Promise.all([...list].map(async f => ({ name: f.name, ...(await readSheet(f)) })));
      const f = fromFullbay(sheets);
      // Fullbay's inventory file has $0 for most sell prices (it calculates them) — use the price each part was last sold for.
      const last = lastPrices(sheets);
      f.parts.forEach(p => { const l = last.get(String(p.sku).toLowerCase()); if (!Number(p.price) && l) p.price = (l.price / 100).toFixed(2); });
      setPrices([...last.values()].map(x => ({ sku: x.sku, price: x.price }))); setFill(null);
      setFb(f); setCheck(null); setDone(null);
      // Old invoices + payments (Reports → All Revenue Report / Payments Report) ride along in the same pick.
      const h = sheets.some(x => historyKind(x.headers)) ? historyFromFullbay(sheets) : null;
      setHist(h && h.invoices.length ? h : null);
      setKinds(Object.fromEntries(sheets.map(x => { const k = historyKind(x.headers); return [x.name, k ? HIST_LABEL[k] : '']; })));
    } catch (e) { toast((e as Error).message, 'err'); }
  }
  async function runAll(dry: boolean) {
    const out: Record<string, Result> = {};
    for (const { kind, label } of FB_ORDER) {
      const rows = (fb as FullbayResult)[kind as 'customers'];
      const total: Result = { created: 0, updated: 0, skipped: 0, errors: [], newCustomers: 0 };
      for (let i = 0; i < rows.length; i += CHUNK) {
        setBusy(`${dry ? 'Checking' : 'Importing'} ${label.toLowerCase()} — ${Math.min(i + CHUNK, rows.length)} of ${rows.length}…`);
        const r = await api<Result>(`/api/import/${kind}`, { body: { rows: rows.slice(i, i + CHUNK), dry, first: i + 2 } });
        total.created += r.created; total.updated += r.updated; total.skipped += r.skipped; total.errors.push(...r.errors); total.newCustomers! += r.newCustomers || 0;
      }
      out[kind] = total;
    }
    if (hist) {
      const total: Result = { created: 0, updated: 0, skipped: 0, errors: [], newCustomers: 0 };
      const inv = hist.invoices as HistInvoice[];
      let owed = 0, pays = 0;
      for (let i = 0; i < inv.length; i += 40) {
        setBusy(`${dry ? 'Checking' : 'Importing'} old invoices — ${Math.min(i + 40, inv.length)} of ${inv.length}…`);
        const r = await api<{ created: number; skipped: number; payments: number; newCustomers: number; owed: number; errors: { row: string; error: string }[] }>('/api/import-history', { body: { invoices: inv.slice(i, i + 40), dry } });
        total.created += r.created; total.skipped += r.skipped; total.newCustomers! += r.newCustomers; owed += r.owed; pays += r.payments;
        total.errors.push(...r.errors);
      }
      out.history = { ...total, owed, payments: pays } as Result;
    }
    return out;
  }
  const counts = fb ? FB_ORDER.map(k => ({ ...k, n: fb[k.kind as 'customers'].length })) : [];
  const res = done || check;
  return (
    <Panel title="Moving from Fullbay? Bring everything at once">
      <div className="pad">
        <p className="form-note">In Fullbay: <b>Manage Data</b> → download each list — Customers (Existing Customers, Contacts, Addresses, Units), Inventory (Download → "without calculated selling price") and Vendors.
          For old invoices and who owes what: <b>Reports → Revenue → All Revenue Report</b> (your whole date range) → Download <b>Invoice list</b> and <b>Invoice details</b>, and <b>Payments Report</b> → Download <b>Details</b>.
          Then pick <b>all the files together</b> here (hold Ctrl to select several). Nothing is saved until you press Import.</p>
        <label className="btn secondary file-pick"><Icon name="download" size={16} />{fb ? 'Pick the files again' : 'Choose Fullbay files'}
          <input type="file" multiple accept=".csv,.xlsx,.txt,.tsv,text/csv" hidden onChange={e => { void load(e.target.files); e.target.value = ''; }} /></label>
        {fb && <>
          <ul className="list small fb-files">{fb.files.map(f => <li key={f.name}><span>{f.name}</span><span className={'right ' + (f.kind === 'unknown' && !kinds[f.name] ? 'danger' : 'muted')}>{kinds[f.name] || FB_LABEL[f.kind]} · {f.rows} rows</span></li>)}</ul>
          <Table heads={['What', 'Ready to bring in', ...(res ? ['New', 'Updated', 'Same', 'Problems'] : [])]}
            rows={[...counts.map(k => [<b>{k.label}</b>, k.n, ...(res ? [res[k.kind]?.created ?? 0, res[k.kind]?.updated ?? 0, res[k.kind]?.skipped ?? 0, res[k.kind]?.errors.length ? <span className="danger">{res[k.kind].errors.length}</span> : 0] : [])]),
              ...(hist ? [[<><b>Old invoices &amp; payments</b><small className="muted">{dollars(hist.total)} · {hist.payments} payments · {dollars(hist.owed)} still owed ({hist.owing} customers)</small></>, hist.invoices.length,
                ...(res ? [res.history?.created ?? 0, 0, res.history?.skipped ?? 0, res.history?.errors.length ? <span className="danger">{res.history.errors.length}</span> : 0] : [])]] : [])]} />
          {(fb.left.length > 0 || (hist?.notes.length ?? 0) > 0) && <ul className="small muted fb-left">{[...fb.left, ...(hist?.notes || [])].map(x => <li key={x}>{x}</li>)}</ul>}
          {prices.length > 0 && <div className="note-bar fill-prices">
            <span><b>Missing sell prices:</b> Fullbay's inventory file has $0 for most prices (Fullbay calculates them). Parts already in Asena Bay with no price can get the price you last charged in Fullbay ({prices.length} part numbers found in your invoices). Parts that have a price are not touched, and nothing else is imported.
              {fill && <> {fill.done ? '✅ ' : ''}<b>{fill.filled}</b> part{fill.filled === 1 ? '' : 's'} {fill.done ? 'got' : 'would get'} a price{fill.stillEmpty ? ` · ${fill.stillEmpty} still without (never sold — use Settings → Parts pricing)` : ''}.</>}</span>
            {!fill?.done && <Button onClick={async () => {
              try { const dry = !fill; const r = await api<{ filled: number; stillEmpty: number }>('/api/parts/fill-prices', { body: { prices, dry } }); setFill({ ...r, done: !dry }); if (!dry) toast(`${r.filled} parts got their sell price`); }
              catch (e) { toast((e as Error).message, 'err'); }
            }}>{fill ? `Fill in ${fill.filled} prices` : 'Only fill in missing sell prices'}</Button>}
          </div>}
          {hist?.missing === 'paymentDetails' && <p className="danger small">Payments file missing — every old invoice would show as paid with no date or method. Add Reports → Payments Report → Download "Details".</p>}
          {hist && <p className="small muted">Old invoices keep their Fullbay numbers and totals and show on the customer portal. They don't change stock or payroll, and nobody gets an email. New invoices continue after the highest Fullbay number.</p>}
          {!fb.customers.length && fb.units.length > 0 && <p className="danger small">The Customers file is missing — the units' companies would be created without phone or email. Add the Customers + Contacts files too.</p>}
          {res && [...FB_ORDER.map(k => k.kind as string), 'history'].map(k => res[k]?.errors.length ? <ErrorList key={k} r={res[k]} /> : null)}
          <div className="actions" style={{ marginTop: 12 }}>
            {busy ? <b>{busy}</b> : done ? <>
              <span>✅ Done — everything from Fullbay is in.</span>
              <Button kind="primary" onClick={() => navigate('/customers')}>Open customers</Button><Button onClick={() => navigate('/inventory')}>Open inventory</Button>
            </> : !check ? (
              <Button kind="primary" onClick={async () => { try { setCheck(await runAll(true)); } catch (e) { toast((e as Error).message, 'err'); } finally { setBusy(''); } }}>Check everything (nothing is saved yet)</Button>
            ) : <>
              <Button kind="primary" onClick={async () => { try { const r = await runAll(false); setDone(r); toast('Fullbay data imported'); } catch (e) { toast((e as Error).message, 'err'); } finally { setBusy(''); } }}>Import all</Button>
              <Button onClick={() => setCheck(null)}>Back</Button>
            </>}
          </div>
        </>}
      </div>
    </Panel>
  );
}
